import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotComics, latestUpdates } from '@/mocks/comics';

const allComics = [...hotComics, ...latestUpdates];

const chapters = [
  { num: 1, title: '一切的开始', date: '2024-01-15' },
  { num: 2, title: '神秘的邂逅', date: '2024-01-22' },
  { num: 3, title: '命运的齿轮', date: '2024-01-29' },
  { num: 4, title: '觉醒的力量', date: '2024-02-05' },
  { num: 5, title: '新的伙伴', date: '2024-02-12' },
  { num: 6, title: '危机四伏', date: '2024-02-19' },
  { num: 7, title: '突破极限', date: '2024-02-26' },
  { num: 8, title: '暗流涌动', date: '2024-03-04' },
  { num: 9, title: '真相浮现', date: '2024-03-11' },
  { num: 10, title: '终极对决', date: '2024-03-18' },
  { num: 11, title: '新的篇章', date: '2024-03-25' },
  { num: 12, title: '不速之客', date: '2024-04-01' },
];

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = allComics.find((c) => c.slug === slug);
  const relatedComics = allComics.filter((c) => c.slug !== slug).slice(0, 6);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-book-2-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 pb-8">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${comic.category === '恋爱' ? 'love' : comic.category === '奇幻' ? 'fantasy' : comic.category === '动作' ? 'action' : comic.category === '悬疑' ? 'thriller' : comic.category === '喜剧' ? 'comedy' : 'drama'}`} className="hover:text-primary-600 transition-colors cursor-pointer">{comic.category}漫画</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-[280px] aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverUrl}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <p className="text-foreground-500 text-sm mb-5">作者：{comic.author}</p>

                <div className="flex items-center gap-4 flex-wrap mb-6">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`text-sm ${i < Math.floor(comic.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                    <span className="ml-1.5 text-foreground-700 font-semibold text-sm">{comic.rating}</span>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">{comic.category}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    <i className={`${comic.status === '连载中' ? 'ri-timer-line' : 'ri-check-double-line'} mr-1`}></i>
                    {comic.status}
                  </span>
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <button className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </button>
                  <button className="px-5 py-3 bg-background-200 text-foreground-700 rounded-full text-sm font-semibold hover:bg-background-300 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                    <i className="ri-heart-line"></i> 加入收藏
                  </button>
                  <button className="px-5 py-3 bg-background-200 text-foreground-700 rounded-full text-sm font-semibold hover:bg-background-300 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">章节目录</h2>
          <div className="bg-background-100 rounded-xl divide-y divide-background-200/70 border border-background-200/60">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 text-xs font-semibold text-foreground-400">
              <span className="col-span-2">序号</span>
              <span className="col-span-7">章节标题</span>
              <span className="col-span-3 text-right">更新时间</span>
            </div>
            {chapters.map((ch) => (
              <Link
                key={ch.num}
                to={`/comic/${comic.slug}`}
                className="grid grid-cols-12 gap-4 px-5 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer items-center"
              >
                <span className="col-span-2 text-sm text-foreground-600 font-medium">第 {ch.num} 话</span>
                <span className="col-span-7 text-sm text-foreground-800 truncate">{ch.title}</span>
                <span className="col-span-3 text-xs text-foreground-400 text-right">{ch.date}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedComics.map((rc) => (
              <Link
                key={rc.id}
                to={`/comic/${rc.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5">
                  <img
                    src={rc.coverUrl}
                    alt={rc.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute top-2 right-2">
                    <span className="px-1.5 py-0.5 rounded bg-background-50/90 text-foreground-900 text-xs font-semibold whitespace-nowrap">★ {rc.rating}</span>
                  </div>
                </div>
                <h3 className="text-foreground-800 font-medium text-sm truncate">{rc.title}</h3>
                <p className="text-foreground-400 text-xs truncate">{rc.author}</p>
              </Link>
            ))}
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${comic.title} - 韩漫网`,
          "author": { "@type": "Person", "name": comic.author },
          "description": comic.description,
          "datePublished": "2024-01-15",
          "dateModified": "2026-07-08",
          "publisher": { "@type": "Organization", "name": "韩漫网", "url": "http://mostlive.cn/" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `http://mostlive.cn/comic/${comic.slug}` }
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://mostlive.cn/" },
            { "@type": "ListItem", "position": 2, "name": `${comic.category}漫画`, "item": `http://mostlive.cn/category/${comic.category === '恋爱' ? 'love' : comic.category === '奇幻' ? 'fantasy' : comic.category === '动作' ? 'action' : comic.category === '悬疑' ? 'thriller' : comic.category === '喜剧' ? 'comedy' : 'drama'}` },
            { "@type": "ListItem", "position": 3, "name": comic.title, "item": `http://mostlive.cn/comic/${comic.slug}` }
          ]
        }) }} />
      </main>
      <Footer />
    </>
  );
}